import { AutoComplete } from 'antd';
import type { AutoCompleteProps } from 'antd';
import { forwardRef } from 'react';
import {
  controlSizeForContext,
  CONTROL_CLASS,
} from '@/theme/controlSizing';
import type { ControlContext } from '@/theme/controlSizing';

/**
 * Props {@link AutoCompleteBox}.
 */
export interface AutoCompleteBoxProps
  extends Omit<AutoCompleteProps, 'size'> {
  /** Konteks pemakaian — menentukan density. Default: `form`. */
  context?: ControlContext;
  /** Override paksa size Antd jika perlu. */
  size?: AutoCompleteProps['size'];
}

/**
 * AutoComplete — wrapper Antd AutoComplete (free-text input dengan suggestion).
 *
 * Beda dengan {@link ComboBox}: AutoComplete mengizinkan **input bebas**
 * (apa pun yang user ketik adalah valid), suggestion hanya membantu. ComboBox
 * memaksa user pilih dari option (kecuali pakai `mode="tags"`).
 *
 * Sifat universal sama dengan family lain: density via `context`, width 100%,
 * class `booku-control`.
 *
 * Pemakaian:
 * ```tsx
 * <AutoCompleteBox
 *   value={teks}
 *   onChange={setTeks}
 *   options={suggestions.map((s) => ({ value: s }))}
 *   placeholder="Ketik atau pilih..."
 * />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/control-system-pattern.md`
 */
export const AutoCompleteBox = forwardRef<
  React.ComponentRef<typeof AutoComplete>,
  AutoCompleteBoxProps
>(function AutoCompleteBox(
  { context = 'form', size, style, className, ...rest },
  ref
) {
  const resolvedSize = size ?? controlSizeForContext(context);
  return (
    <AutoComplete
      ref={ref}
      size={resolvedSize}
      className={`${CONTROL_CLASS}${className ? ` ${className}` : ''}`}
      style={{ width: '100%', ...style }}
      {...rest}
    />
  );
});
